/**
 * HL7 v2 Messaging Configuration Manager
 * Admin page for configuring HL7 MLLP connections, message types, and viewing message logs.
 */

import React, { useState, useEffect } from 'react';
import { Network, Send, RefreshCw, CheckCircle, AlertCircle, X, Eye } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { apiClient } from '@/services/apiClient';

interface HL7Config {
  partner_name: string;
  mllp_host: string;
  mllp_port: number;
  tls_enabled: boolean;
  sending_facility_id: string;
  receiving_facility_id: string;
  receiving_application: string;
  enabled_message_types: string[];
  auto_send_adt: boolean;
  auto_send_ccd: boolean;
}

interface HL7Message {
  id: number;
  message_type: string;
  trigger_event: string;
  direction: string;
  member_id: string;
  status: string;
  ack_code: string;
  created_at: string;
  raw_message?: string;
}

const ALL_MESSAGE_TYPES = ['ADT', 'MDM', 'ORU', 'RDE', 'PPR', 'VXU'];
const TRIGGER_EVENTS = ['A04', 'A08', 'A01', 'A03', 'A28', 'A31'];

export function HL7ConfigManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Connection settings
  const [partnerName, setPartnerName] = useState('LANES_LA');
  const [mllpHost, setMllpHost] = useState('');
  const [mllpPort, setMllpPort] = useState(2575);
  const [tlsEnabled, setTlsEnabled] = useState(false);

  // Facility & message settings
  const [sendingFacilityId, setSendingFacilityId] = useState('');
  const [receivingFacilityId, setReceivingFacilityId] = useState('');
  const [receivingApplication, setReceivingApplication] = useState('');
  const [enabledMessageTypes, setEnabledMessageTypes] = useState<string[]>([]);
  const [autoSendAdt, setAutoSendAdt] = useState(false);
  const [autoSendCcd, setAutoSendCcd] = useState(false);

  // Test connection
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ status: string; message?: string } | null>(null);

  // Manual send
  const [sendMemberId, setSendMemberId] = useState('');
  const [triggerEvent, setTriggerEvent] = useState('A04');
  const [sendingAdt, setSendingAdt] = useState(false);
  const [sendingCcd, setSendingCcd] = useState(false);
  const [sendResult, setSendResult] = useState<{ status: string; message?: string } | null>(null);

  // Message log
  const [messages, setMessages] = useState<HL7Message[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Detail dialog
  const [selectedMessage, setSelectedMessage] = useState<HL7Message | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchMessages();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.get('/api/hl7/config');
      const data = resp.data;
      setPartnerName(data.partner_name || 'LANES_LA');
      setMllpHost(data.mllp_host || '');
      setMllpPort(data.mllp_port ?? 2575);
      setTlsEnabled(data.tls_enabled || false);
      setSendingFacilityId(data.sending_facility_id || '');
      setReceivingFacilityId(data.receiving_facility_id || '');
      setReceivingApplication(data.receiving_application || '');
      setEnabledMessageTypes(data.enabled_message_types || []);
      setAutoSendAdt(data.auto_send_adt || false);
      setAutoSendCcd(data.auto_send_ccd || false);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load HL7 configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    setLoadingMessages(true);
    try {
      const resp = await apiClient.get('/api/hl7/messages');
      setMessages(resp.data.messages || resp.data || []);
    } catch {
      // silent
    } finally {
      setLoadingMessages(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.put('/api/hl7/config', {
        partner_name: partnerName,
        mllp_host: mllpHost,
        mllp_port: mllpPort,
        tls_enabled: tlsEnabled,
        sending_facility_id: sendingFacilityId,
        receiving_facility_id: receivingFacilityId,
        receiving_application: receivingApplication,
        enabled_message_types: enabledMessageTypes,
        auto_send_adt: autoSendAdt,
        auto_send_ccd: autoSendCcd,
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await apiClient.post('/api/hl7/test-connection');
      setTestResult(resp.data);
    } catch (err: any) {
      setTestResult({ status: 'error', message: err?.response?.data?.error || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const sendAdt = async () => {
    setSendingAdt(true);
    setSendResult(null);
    try {
      const resp = await apiClient.post('/api/hl7/send-adt', {
        member_id: sendMemberId,
        trigger_event: triggerEvent,
      });
      setSendResult(resp.data);
      fetchMessages();
    } catch (err: any) {
      setSendResult({ status: 'error', message: err?.response?.data?.error || 'Failed to send ADT' });
    } finally {
      setSendingAdt(false);
    }
  };

  const sendCcd = async () => {
    setSendingCcd(true);
    setSendResult(null);
    try {
      const resp = await apiClient.post('/api/hl7/send-ccd', {
        member_id: sendMemberId,
      });
      setSendResult(resp.data);
      fetchMessages();
    } catch (err: any) {
      setSendResult({ status: 'error', message: err?.response?.data?.error || 'Failed to send CCD' });
    } finally {
      setSendingCcd(false);
    }
  };

  const viewMessageDetail = async (msg: HL7Message) => {
    setSelectedMessage(msg);
    setLoadingDetail(true);
    try {
      const resp = await apiClient.get(`/api/hl7/messages/${msg.id}`);
      setSelectedMessage(resp.data);
    } catch {
      // keep partial data
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggleMessageType = (type: string) => {
    setEnabledMessageTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading HL7 configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Network className="h-7 w-7 text-blue-600" />
        <div>
          <h2 className="text-2xl font-bold">HL7 v2 Messaging</h2>
          <p className="text-sm text-muted-foreground">
            Configure MLLP connections, message types, and monitor HL7 message exchange
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Connection Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Connection Settings</CardTitle>
          <CardDescription>MLLP connection parameters for the HL7 interface engine</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="partner_name">Partner Name</Label>
              <Input
                id="partner_name"
                value={partnerName}
                onChange={(e) => setPartnerName(e.target.value)}
                placeholder="LANES_LA"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mllp_host">MLLP Host</Label>
              <Input
                id="mllp_host"
                value={mllpHost}
                onChange={(e) => setMllpHost(e.target.value)}
                placeholder="hl7.example.org"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mllp_port">MLLP Port</Label>
              <Input
                id="mllp_port"
                type="number"
                value={mllpPort}
                onChange={(e) => setMllpPort(parseInt(e.target.value) || 2575)}
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={tlsEnabled} onCheckedChange={setTlsEnabled} id="tls_enabled" />
              <Label htmlFor="tls_enabled">TLS Enabled</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Facility & Message Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Facility & Message Settings</CardTitle>
          <CardDescription>HL7 facility identifiers and message type configuration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="sending_facility_id">Sending Facility ID</Label>
              <Input
                id="sending_facility_id"
                value={sendingFacilityId}
                onChange={(e) => setSendingFacilityId(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="receiving_facility_id">Receiving Facility ID</Label>
              <Input
                id="receiving_facility_id"
                value={receivingFacilityId}
                onChange={(e) => setReceivingFacilityId(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="receiving_application">Receiving Application</Label>
              <Input
                id="receiving_application"
                value={receivingApplication}
                onChange={(e) => setReceivingApplication(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Enabled Message Types</Label>
            <div className="flex flex-wrap gap-3">
              {ALL_MESSAGE_TYPES.map((type) => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabledMessageTypes.includes(type)}
                    onChange={() => toggleMessageType(type)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">{type}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex items-center gap-3">
              <Switch checked={autoSendAdt} onCheckedChange={setAutoSendAdt} id="auto_send_adt" />
              <Label htmlFor="auto_send_adt">Auto-send ADT</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={autoSendCcd} onCheckedChange={setAutoSendCcd} id="auto_send_ccd" />
              <Label htmlFor="auto_send_ccd">Auto-send CCD</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save & Test */}
      <div className="flex items-center gap-3">
        <Button onClick={saveConfig} disabled={saving}>
          {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Configuration
        </Button>
        <Button variant="outline" onClick={testConnection} disabled={testing}>
          {testing ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Network className="mr-2 h-4 w-4" />}
          Test Connection
        </Button>
        {testResult && (
          <Badge variant={testResult.status === 'ok' || testResult.status === 'success' ? 'default' : 'destructive'}>
            {testResult.status === 'ok' || testResult.status === 'success' ? (
              <CheckCircle className="mr-1 h-3 w-3" />
            ) : (
              <AlertCircle className="mr-1 h-3 w-3" />
            )}
            {testResult.message || testResult.status}
          </Badge>
        )}
      </div>

      {/* Manual Send */}
      <Card>
        <CardHeader>
          <CardTitle>Manual Send</CardTitle>
          <CardDescription>Send individual HL7 messages for testing or manual workflows</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="send_member_id">Member ID</Label>
              <Input
                id="send_member_id"
                value={sendMemberId}
                onChange={(e) => setSendMemberId(e.target.value)}
                placeholder="Enter member ID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trigger_event">Trigger Event</Label>
              <select
                id="trigger_event"
                value={triggerEvent}
                onChange={(e) => setTriggerEvent(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
              >
                {TRIGGER_EVENTS.map((evt) => (
                  <option key={evt} value={evt}>
                    {evt}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={sendAdt} disabled={sendingAdt || !sendMemberId}>
                {sendingAdt ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send ADT
              </Button>
              <Button variant="outline" onClick={sendCcd} disabled={sendingCcd || !sendMemberId}>
                {sendingCcd ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send CCD
              </Button>
            </div>
          </div>
          {sendResult && (
            <div
              className={`flex items-center gap-2 rounded-md p-3 text-sm ${
                sendResult.status === 'ok' || sendResult.status === 'success'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}
            >
              {sendResult.status === 'ok' || sendResult.status === 'success' ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              {sendResult.message || sendResult.status}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message Log */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Message Log</CardTitle>
            <CardDescription>Recent HL7 messages sent and received</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchMessages} disabled={loadingMessages}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loadingMessages ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>ACK</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      No messages found
                    </TableCell>
                  </TableRow>
                ) : (
                  messages.map((msg) => (
                    <TableRow
                      key={msg.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => viewMessageDetail(msg)}
                    >
                      <TableCell className="font-mono text-sm">{msg.message_type}</TableCell>
                      <TableCell className="font-mono text-sm">{msg.trigger_event}</TableCell>
                      <TableCell>
                        <Badge variant={msg.direction === 'outbound' ? 'default' : 'secondary'}>
                          {msg.direction}
                        </Badge>
                      </TableCell>
                      <TableCell>{msg.member_id}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            msg.status === 'accepted' || msg.status === 'sent'
                              ? 'default'
                              : msg.status === 'error' || msg.status === 'rejected'
                              ? 'destructive'
                              : 'secondary'
                          }
                        >
                          {msg.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{msg.ack_code}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(msg.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Message Detail Dialog */}
      {selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-2xl rounded-lg bg-background p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                Message Detail - {selectedMessage.message_type}^{selectedMessage.trigger_event}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedMessage(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="mb-3 flex gap-2">
              <Badge variant={selectedMessage.direction === 'outbound' ? 'default' : 'secondary'}>
                {selectedMessage.direction}
              </Badge>
              <Badge
                variant={
                  selectedMessage.status === 'accepted' || selectedMessage.status === 'sent'
                    ? 'default'
                    : 'destructive'
                }
              >
                {selectedMessage.status}
              </Badge>
              {selectedMessage.ack_code && (
                <Badge variant="outline">ACK: {selectedMessage.ack_code}</Badge>
              )}
            </div>
            <div className="text-sm text-muted-foreground mb-2">
              Member: {selectedMessage.member_id} | Time: {new Date(selectedMessage.created_at).toLocaleString()}
            </div>
            {loadingDetail ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <pre className="max-h-80 overflow-auto rounded-md bg-muted p-4 text-xs font-mono whitespace-pre-wrap">
                {selectedMessage.raw_message || 'No raw message data available'}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
